// ─────────────────────────────────────────────
//  assignChoices — WHICH options the Assign menu offers, decided in ONE place.
//
//  The menu (`components/BenchAssignFlow`) is opened from three different
//  positions now: the OR Board's Unassigned list, where the person is on the
//  bench; a SEAT name on the Timeline chart, where they hold a room; and — since
//  the chart grew two lanes — the SECOND name on a paired bar, where they are in
//  a room they do not hold. The same five buttons cannot be right for all three.
//
//  ⚠️ THE POSITION IS AN ENUM, NOT A BOOLEAN, AND THAT IS THE WHOLE BUG FIX.
//  This module used to take `inRoom`, which quietly meant two different things —
//  "is standing in an OR" and "holds the room" — and the second provider is the
//  one person for whom those differ. Opened on her, the menu offered "Assign
//  Supervising CRNA" and read "Swap Kayla Gibbons for somebody else beside Kayla
//  Gibbons", whose write would have replaced the very person the operator
//  clicked. A third value, not a second condition: the moment there are three
//  positions, a boolean has to lie about one of them.
//
//  ⚠️ AND A SECOND, INDEPENDENT AXIS: IS THIS PERSON IDENTIFIED? The owner
//  reported that "some crnas in the timeline when you press dont work" — the
//  names on a legacy two-person cell, which resolve to no staff record, so the
//  chart drew them as plain text rather than open a menu that could only write
//  to `undefined`. `EDIT_CRNA` is the option that fixes that: it targets the
//  assignment row's SLOT and needs no id, so an unidentified name now opens a
//  menu offering exactly the one thing that works.
//
//  ⚠️ BUT `identified: false` IS THE LAST RESORT, NOT THE FIRST ANSWER, AND
//  THAT WAS THE NEXT REPORT: *"why are they classified diferent … all the crna
//  need to have the same privileges"*. Five CRNAs were getting the one-option
//  menu while their colleague next door got five — not because of anything
//  about them, but because their sheet cell carried an annotation the importer
//  could not parse, so the row stored no id. The CALLER now resolves the name
//  against the staff pool first (`secondProviderWrite.resolveBarIdentity`), and
//  what still arrives here unidentified is genuinely unidentifiable: nobody
//  answers to the name, or several people do and picking one would be a guess.
//  This module's job is unchanged — it is the answer that got rarer.
// ─────────────────────────────────────────────

export const NEW_ROOM = "new_room";
export const TAKEOVER = "takeover";
export const SECOND_CRNA = "second_crna";
export const EDIT_CRNA = "edit_crna";
export const UNASSIGN = "unassign";
export const BREAKS = "breaks";
export const HOME = "home";
export const TRANSFER = "transfer";

// Where this provider is standing when the menu opens.
export const BENCH = "bench";   // free — in no room at all
export const SEAT = "seat";     // holds a room (assignment row `ahpId`)
export const SECOND = "second"; // in a room as the SECOND provider, holding none

// Returns the ordered option ids for this provider, in the order the menu
// renders them.
//
//   position             — BENCH | SEAT | SECOND, above.
//   identified           — this provider resolves to a REAL staff/roster record,
//                          so a write may be keyed by their id. See below: this
//                          is NOT "the id is non-empty".
//   givingBreaks         — the GIVING BREAKS label is currently on.
//   takeoverTargets      — rooms held by SOMEBODY ELSE, which is what a takeover
//                          needs. Zero means the step could only show its empty
//                          state, so the option is not offered at all.
//   secondCrnaCandidates — CRNAs on today's roster who could stand with them.
//   transferTargets      — other active facilities.
export function assignChoices({
  position = BENCH,
  identified = true,
  givingBreaks = false,
  takeoverTargets = 0,
  secondCrnaCandidates = 0,
  transferTargets = 0,
} = {}) {
  const out = [];
  // ⚠️ `identified` GATES EVERY OPTION THAT NAMES A PERSON, AND THAT IS THE
  // SECOND HALF OF THIS MODULE'S JOB. Each of the five below writes a roster
  // document, a transfer, or an assignment row KEYED BY THE PROVIDER'S ID. A
  // legacy "X orient Y" cell names two people and identifies neither, so the
  // chart used to render both names as plain text — a menu whose every option
  // wrote to `undefined` is worse than no menu at all.
  //
  // ⚠️ AND "identified" IS NOT "the id is non-empty". `secondProviderMember`
  // mints a synthetic `sp:<name>` id for a person nobody resolved, because a
  // roster member with `id: undefined` collides with every other one. It is
  // truthy. The caller decides — `secondProviderWrite.isResolvedProvider` — and
  // hands the answer in as a boolean, because this module stays pure.
  if (identified) {
    // ⚠️ BENCH ONLY, INCLUDING FOR THE SECOND PROVIDER — who does not hold a
    // room and for whom a room of her own is a perfectly reasonable clinical
    // move, but NOT one this menu can make correctly. Every option here that
    // MOVES her must also take her out of the room she is standing in, and
    // `onNewRoom` hands off to a modal that cannot: she would end up named as
    // the second provider in OR 9 and as the seat in OR 14 at the same time.
    // The path that works is remove-then-place — the room modal has a control
    // named for exactly that. Takeover is the one exception, because it can
    // clear her on the same write.
    if (position === BENCH) out.push(NEW_ROOM);
    if (takeoverTargets > 0) out.push(TAKEOVER);
    // Only the SEAT has an empty place beside them. Offered to the bench it has
    // no room to fill; offered to the second provider it means "replace me",
    // which is the opposite of what it says — one verb, two actions.
    if (position === SEAT && secondCrnaCandidates > 0) out.push(SECOND_CRNA);
  }

  // ── THE ONE OPTION THAT NEEDS NO STAFF ID ────────────────────────────────
  //
  // A leaving time is a different kind of fact from everything around it: it is
  // a field on the ASSIGNMENT ROW'S SLOT — `ahpDepartTime` for the seat,
  // `orienteeDepartTime` for the second provider — addressed by WHICH CHAIR the
  // person is sitting in, not by who they are. So it is the one edit that still
  // works on a row nobody ever resolved, which is also, not coincidentally, the
  // row whose leaving time is missing and needs typing in.
  //
  // ⚠️ POSITION, NOT IDENTITY, IS ITS ONLY CONDITION. Not offered on the BENCH:
  // no row, therefore no slot, therefore nothing to target. Deliberately placed
  // AFTER the room options and BEFORE the ones that move the person, so the
  // five that were already here keep the order the operator learned.
  if (position !== BENCH) out.push(EDIT_CRNA);

  // ── TAKE THEM OUT OF THE ROOM ────────────────────────────────────────────
  //
  // Owner, 2026-09-09, with the menu open on the second CRNA in WOR 9: *"Can you
  // add unassigned"*. Every other option here either moved the person somewhere
  // ELSE (a takeover, home, another campus) or changed a fact about them. There
  // was no way to simply take them out and leave them on today's roster — the
  // one move an AIC makes most.
  //
  // ⚠️ SECOND ONLY, AND THAT IS NOT A HALF-MEASURE. Clearing the second
  // provider is ONE field group on the assignment row (`clearSecondProviderPatch`)
  // and the seat still holds the room, so nothing else on the board has to move.
  // Taking the SEAT out is a different act with four coordinated writes — the
  // row, the room doc's `providerId`/`providerName` mirror (without which
  // RoomCard still shows them and the room reads as staffed when it is not), the
  // FREE NOW stamp, and the disposition — and `TimelineCaseModal` already
  // implements exactly that, reached by clicking the bar itself. A second copy
  // here would be the same act with two implementations, and the one that
  // forgets the mirror is the one that ships a room nobody is in.
  //
  // ⚠️ AND IT NEEDS NO STAFF ID, for the same reason `EDIT_CRNA` does not: it
  // writes the assignment row's SLOT, addressed by which chair the person is
  // sitting in, not a document keyed by who they are. So it sits OUTSIDE the
  // `identified` gate — a legacy "X orient Y" cell that resolved nobody can
  // still be emptied.
  if (position === SECOND) out.push(UNASSIGN);

  if (identified) {
    // ⚠️ OFFERED IN EVERY POSITION, AND IT DID NOT USED TO BE. This was
    // BENCH-only on the reading that "they stay on the free list, labelled
    // GIVING BREAKS" is a sentence you cannot say to somebody standing in an
    // OR. But BoardPage has always built its cover list the other way — "a
    // CRNA already in a room can still give a colleague a break" — so the two
    // halves of the app disagreed, and the half that decided who could be
    // LABELLED was the restrictive one. Owner settled it 2026-09-07: a person
    // holding a room can be a break-giver.
    //
    // What makes that honest rather than decorative is that `breakGivers`
    // (domain/bench.js) now returns assigned people too, carrying the room
    // they are standing in — so the label lights up a list somebody reads,
    // instead of setting a flag no surface renders.
    out.push(BREAKS);
    out.push(HOME);
    if (transferTargets > 0) out.push(TRANSFER);
  }
  return out;
}
